import * as React from 'react';
import { Field, Form, FormSpy } from 'react-final-form';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '../modules/components/Typography';
import AppFooter from '../modules/views/AppFooter';
import AppAppBar from '../modules/views/AppAppBar';
import AppForm from '../modules/views/AppForm';
import { email, required } from '../modules/form/validation';
import RFTextField from '../modules/form/RFTextField';
import FormButton from '../modules/form/FormButton';
import FormFeedback from '../modules/form/FormFeedback';
import { useLocation, useNavigate } from 'react-router-dom';
import { getUserCurrentlyAuthenticated, userSignIn } from '../modules/components/authentication';
import { FORM_ERROR } from 'final-form';
import Snackbar from '../modules/components/Snackbar';
import PasswordRFTextField from '../modules/components/PasswordRFTextField';
import logger from '../modules/components/logger';

function SignIn() {
  const {state} = useLocation();
  const initialValueEmail = state?.userEmail;
  
  const [open, setOpen] = React.useState(state?.reset);
  const [isValid, setIsValid] = React.useState(false);
  const [userEmail, setUserEmail] = React.useState(initialValueEmail || "");

  const handleClickForgotPassword = () => {
    navigate('/forgot-password', { state: { userEmail } })
  }

  const handleClickSignUp = () => {
    navigate('/sign-up', { state: { userEmail } })
  }

  const handleClose = () => {
    setOpen(false);
  }

  const navigate = useNavigate();

  React.useEffect(() => {
    getUserCurrentlyAuthenticated()
      .then(() => navigate("/dashboard"))
      .catch(logger.info);
  }, [navigate]);

  const validate = (values) => {
    const errors = required(['email', 'password'], values);

    if (!errors.email) {
      const emailError = email(values.email);
      if (emailError) {
        errors.email = emailError;
      }
    }

    setIsValid(Object.keys(errors).length === 0);

    return errors;
  };

  const handleSubmit = ({email, password}) => {
    return userSignIn(email, password)
      .then(() => navigate("/dashboard"))
      .catch((reason) => {
        const exception = reason.code;
        logger.error(exception);
        let message = "Could not sign in.";
        if (exception === "UserNotFoundException")
          message = "User email is incorrect.";
        if (reason.code === "NotAuthorizedException")
          message = "User password is incorrect."
        return {[FORM_ERROR]: message};
      });
  };

  return (
    <React.Fragment>
      <AppAppBar />
      <AppForm>
        <React.Fragment>
          <Typography variant="h3" gutterBottom marked="center" align="center">
            Sign In
          </Typography>
          <Typography variant="body2" align="center">
            {'Not a member yet? '}
            <Link 
              underline="always" 
              variant="body2" component="button" 
              onClick={handleClickSignUp}
              style={{paddingBottom: "2px"}}
            >
              Sign up here.
            </Link>
          </Typography>
        </React.Fragment>
        <Form
          onSubmit={handleSubmit}
          subscription={{ submitting: true, dirty: true }}
          validate={validate}
        >
          {({ handleSubmit: handleSubmit2, submitting, dirty }) => (
            <Box component="form" onSubmit={handleSubmit2} noValidate sx={{ mt: 6 }}>
              <Field
                autoComplete="email"
                autoFocus
                component={RFTextField}
                initialValue={initialValueEmail}
                disabled={submitting}
                fullWidth
                label="Email"
                margin="normal"
                name="email"
                required
                size="large"
                onInput={e => setUserEmail(e.target.value)}
              />
              <PasswordRFTextField
                name="password"
                label="Password"
                disabled={submitting}
                autoComplete="password"
                dirty={dirty}
              />
              <FormSpy subscription={{ submitError: true }}>
                {({ submitError }) =>
                  submitError ? (
                    <FormFeedback error sx={{ mt: 2 }}>
                      {submitError}
                    </FormFeedback>
                  ) : null
                }
              </FormSpy>
              <FormButton
                sx={{ mt: 3, mb: 2 }}
                disabled={!isValid || submitting}
                size="large"
                color="secondary"
                fullWidth
              >
                {submitting ? 'In progress…' : 'Sign In'}
              </FormButton>
            </Box>
          )}
        </Form>
        <Typography align="center">
          <Link 
            underline="always" 
            variant="body1" component="button" 
            onClick={handleClickForgotPassword}
            style={{paddingBottom: "2px"}}
          >
            Forgot password?
          </Link>
        </Typography>
      </AppForm>
      <AppFooter />
      <Snackbar 
        open={open}
        closeFunc={handleClose}
        message="Your password was successfully reset."
      />
    </React.Fragment>
  );
}

export default SignIn;